import { Loading } from 'components/common/Loading';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { getActiveTrustSite, getSiteById } from 'services/sites';
import { ISiteContext, SetActiveSiteOptions, SiteData } from 'types/sites';
import { errorToast } from 'utils/toast';
import { useAuth } from './AuthContext';
import { TrustData } from '../types/trusts';
import { useUpdateUserActiveSiteMutation } from '../query/users';
import { QueryClient, useQueryClient } from 'react-query';
import { useHandleSiteTranslationResource } from '../hooks/i18n/useHandleSiteTranslationResource';
import { isSiteExpired } from '../utils/siteUtils';
import { getUserProfile, updateUserActiveSite } from '../services/userProfile';
import { useOnWindowVisible } from '../hooks/util/useOnWindowVisible';
import { BlockingView } from '../components/Layout/BlockingView';
import { useLoadingTimeSplit } from '../hooks/useLoadingTimeSplit';

const defaultContext: ISiteContext = {
  allSites: undefined,
  activeSite: {} as SiteData,
  activeTrust: {} as TrustData,
  setActiveSite: undefined as any,
  setActiveSiteById: undefined as any,
};

export const SiteContext = React.createContext<ISiteContext>(defaultContext);
SiteContext.displayName = 'Site Context'; // Only for debugging

export const useSite = () => useContext(SiteContext);

const useOnBecomingVisibleCheckForActiveSiteAndUpdateIfChanged = ({
  queryClient,
}: {
  queryClient: QueryClient;
}) => {
  const { sangixUser } = useAuth();
  const [isUpdateCheckInProgress, setIsUpdateCheckInProgress] = useState(false);
  useOnWindowVisible(() => {
    const checkSite = async () => {
      if (!sangixUser) {
        return;
      }
      setIsUpdateCheckInProgress(true);
      try {
        const { data: latestUser } = await getUserProfile();
        if (latestUser.active_site_id !== sangixUser.active_site_id) {
          await queryClient.cancelQueries();
          await updateUserActiveSite(sangixUser.active_site_id);
          await queryClient.invalidateQueries();
        }
      } catch (e) {
        console.error('Failed to sync active site', e);
      } finally {
        setIsUpdateCheckInProgress(false);
      }
    };
    checkSite();
  });
  return { isUpdateCheckInProgress };
};

export const SiteProvider: React.FC = ({ children }) => {
  const { sangixUser, updateAuthUser, logout } = useAuth();
  const [allSites, setAllSites] = useState<SiteData[]>();
  const [activeSite, setActiveSiteState] = useState<SiteData>();
  const [activeTrust, setActiveTrust] = useState<TrustData>();
  const [isSiteLoading, setIsSiteLoading] = useState(false);
  const { mutateAsync: updateUserActiveSite } =
    useUpdateUserActiveSiteMutation();
  const queryClient = useQueryClient();

  const setActiveSite = useCallback(
    async (
      site: SiteData,
      { save = true, showLoader = true }: SetActiveSiteOptions = {},
    ): Promise<void> => {
      if (showLoader) setIsSiteLoading(true);
      try {
        if (save) {
          await updateUserActiveSite(site.id);
          updateAuthUser({
            sangixUser: {
              ...sangixUser!!,
              active_site_id: site.id,
            },
          });
        }
        setActiveSiteState(site);
        /**
         * Changing active site may cause a different response from BE on endpoints with same params. Therefore, we want to reload all queries.
         */
        if (save) queryClient.invalidateQueries();
      } catch (err) {
        if (save) {
          const originalSiteId = sangixUser!.active_site_id;
          updateAuthUser({
            sangixUser: {
              ...sangixUser!!,
              active_site_id: originalSiteId,
            },
          });
        }
      } finally {
        setIsSiteLoading(false);
      }
    },
    [
      queryClient,
      setIsSiteLoading,
      setActiveSiteState,
      updateAuthUser,
      sangixUser,
      updateUserActiveSite,
    ],
  );

  useHandleSiteTranslationResource(activeSite);

  const setActiveSiteById = useCallback(
    async (siteId: string, options: SetActiveSiteOptions = {}) => {
      let site = allSites?.find((site) => site.id === siteId);
      try {
        if (!site) {
          const siteResponse = await getSiteById(siteId);
          queryClient.removeQueries();
          site = siteResponse.data;
        }
        await setActiveSite(site, options);
        return site;
      } catch (err) {
        errorToast(err);
      }
    },
    [allSites, queryClient, setActiveSite],
  );

  const { isUpdateCheckInProgress } =
    useOnBecomingVisibleCheckForActiveSiteAndUpdateIfChanged({
      queryClient,
    });
  const {
    isLoadingBeforeDelay: isUpdateCheckInReasonableTime,
    isLoadingAfterDelay: isUpdateCheckSlow,
  } = useLoadingTimeSplit(isUpdateCheckInProgress);

  useEffect(() => {
    (async () => {
      if (sangixUser) {
        try {
          const { data } = await getActiveTrustSite();
          let activeSite = data.data?.find(
            (site) => site.id === sangixUser?.active_site_id,
          );
          if (!activeSite) {
            const siteById = await getSiteById(sangixUser.active_site_id);
            if (isSiteExpired(siteById.data)) {
              activeSite = siteById.data;
            } else {
              console.error('User site not found, just setting first site');
              activeSite =
                data.data?.filter((site) => !isSiteExpired(site))[0] ||
                data.data?.[0];
            }
          }
          setAllSites(data.data);
          setActiveSiteState(activeSite);
          setActiveTrust(data.trust);
        } catch (err) {
          errorToast(err);
          logout();
        }
      } else {
        setActiveSiteState(undefined);
      }
    })();
  }, [logout, sangixUser]);

  if (sangixUser && !allSites && !activeSite) {
    return <Loading />;
  }

  if (Boolean(sangixUser) && !Boolean(activeSite)) {
    return <Loading />;
  }

  return (
    <SiteContext.Provider
      value={{
        activeSite: activeSite as SiteData,
        activeTrust: activeTrust as TrustData,
        setActiveSite,
        allSites,
        setActiveSiteById,
      }}
    >
      {(isSiteLoading || isUpdateCheckSlow) && <Loading />}
      {isUpdateCheckInReasonableTime && <BlockingView />}
      {children}
    </SiteContext.Provider>
  );
};
